import { getCurrentEnvironment, subscribeToEnvChanges } from '../../config/environment';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Get environment configuration
let env = getCurrentEnvironment();

// Token cache to minimize API calls
const tokenCache = {
  accessToken: null,
  expiresAt: null
};

// Define Lulu API configuration based on environment
let LULU_API = {
  BASE_URL: env.lulu.baseUrl,
  TOKEN_URL: env.lulu.tokenUrl,
  PRINT_JOB_COST_URL: env.lulu.printJobCostUrl,
  USE_SANDBOX: env.lulu.USE_SANDBOX
};

// Subscribe to environment changes
subscribeToEnvChanges((newEnv) => {
  console.log('Lulu API environment changed:', newEnv.name);
  // Update Lulu API configuration when environment changes
  LULU_API = {
    BASE_URL: newEnv.lulu.baseUrl,
    TOKEN_URL: newEnv.lulu.tokenUrl,
    PRINT_JOB_COST_URL: newEnv.lulu.printJobCostUrl,
    USE_SANDBOX: newEnv.lulu.USE_SANDBOX
  };
});

export async function getAccessToken() {
  // Following official Lulu API documentation for token generation
  // Check if we already have a valid token in memory cache
  if (tokenCache.accessToken && tokenCache.expiresAt && Date.now() < tokenCache.expiresAt) {
    console.log('Using cached Lulu API token, expires in', 
      Math.round((tokenCache.expiresAt - Date.now()) / 1000), 'seconds');
    return tokenCache.accessToken;
  }

  console.log('🔑 Getting new Lulu API access token...');
  console.log('🔑 Using environment:', env.name);
  console.log('🔑 Sandbox mode:', LULU_API.USE_SANDBOX ? 'YES' : 'NO');

  try {
    // Determine which environment to use
    const environment = LULU_API.USE_SANDBOX ? 'sandbox' : 'production';
    console.log(`🔑 Using ${environment} environment for authentication`);
    
    // First try with the serverless function
    try {
      console.log('🔑 Using serverless function for authentication');
      const serverlessFunctionUrl = '/.netlify/functions/lulu-auth';
      
      const response = await fetch(serverlessFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Serverless auth failed:', errorText);
        throw new Error(`Serverless authentication failed: ${errorText}`);
      }
      
      const tokenData = await response.json();
      const newToken = tokenData.access_token;
      const expiresIn = tokenData.expires_in || 3600; // Default to 1 hour if not specified
      
      // Update the token cache
      tokenCache.accessToken = newToken;
      tokenCache.expiresAt = Date.now() + (expiresIn * 1000); // Convert seconds to milliseconds
      
      console.log('✅ Got new token via serverless function, expires in', expiresIn, 'seconds');
      return newToken;
    } catch (serverlessError) {
      console.error('Serverless function error:', serverlessError.message);
      console.error('❌ Serverless function failed - no fallback available');
      throw new Error(`Authentication failed: ${serverlessError.message}`);
    }
  } catch (error) {
    console.error('Authentication error:', error);
    throw new Error(`Failed to authenticate with Lulu API: ${error.message}`);
  }
}

export async function getPrintJobStatus(printJobId) {
  try {
    const environment = LULU_API.USE_SANDBOX ? 'sandbox' : 'production';
    console.log(`🔍 Getting print job status for ID: ${printJobId} in ${environment.toUpperCase()} environment`);
    
    // First try with a serverless function to avoid CORS issues (like we do with createPrintJob)
    try {
      console.log('🔍 Using serverless function for print job status');
      const serverlessFunctionUrl = '/.netlify/functions/lulu-print-job-status';
      
      const response = await fetch(`${serverlessFunctionUrl}?printJobId=${printJobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('🔍 Serverless print job status response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Serverless print job status failed:', errorText);
        throw new Error(`Serverless print job status failed: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('🔍 Print job status data from serverless:', data);
      return data;
    } catch (serverlessError) {
      console.error('Serverless function error:', serverlessError.message);
      console.log('� Falling back to direct API call with CORS proxy...');
      
      // Fall back to direct API with CORS proxy (existing implementation)
      const accessToken = await getAccessToken();
      const corsProxyUrl = 'https://corsproxy.io/?';
  
      // Use the appropriate endpoint based on environment
      const endpoint = '/print-jobs/';
      console.log(`🔍 Using ${LULU_API.USE_SANDBOX ? 'SANDBOX' : 'PRODUCTION'} Lulu API endpoint`);
      console.log(`🔍 API Base URL: ${LULU_API.BASE_URL}`);
      
      // Build the target URL and use CORS proxy
      // Ensure we have the correct format: base_url + /print-jobs/ + print_job_id
      const baseUrl = LULU_API.BASE_URL.endsWith('/') ? 
        LULU_API.BASE_URL.slice(0, -1) : LULU_API.BASE_URL;
      const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const targetUrl = `${baseUrl}${formattedEndpoint}${printJobId}`;
      
      console.log('🔍 Direct API URL:', targetUrl);
      const proxyUrl = `${corsProxyUrl}${encodeURIComponent(targetUrl)}`;
      console.log('🔍 Using CORS proxy for print job status');
      console.log('🔍 Full proxy URL:', proxyUrl);
      
      const response = await fetch(
        proxyUrl,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Print job status error:', errorText);
        let errorMessage = 'Failed to get print job status';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
  
      const data = await response.json();
      console.log('Print job status data from CORS proxy:', data);
      return data;
    }
  } catch (error) {
    console.error('Error getting print job status:', error);
    throw error;
  }
}

/**
 * Maps shipping method/level to a valid Lulu API enum
 * Required values: "MAIL" "PRIORITY_MAIL" "GROUND_HD" "GROUND_BUS" "GROUND" "EXPEDITED" "EXPRESS"
 * @param {Object|string} shippingMethod - The shipping method object or string
 * @returns {string} A valid shipping level enum value
 */
function mapShippingLevelToEnum(shippingMethod) {
  // Default to MAIL if no shipping method provided
  if (!shippingMethod) return 'MAIL';
  
  // If it's a string and matches a valid enum, use it directly
  if (typeof shippingMethod === 'string') {
    const validEnums = ['MAIL', 'PRIORITY_MAIL', 'GROUND_HD', 'GROUND_BUS', 'GROUND', 'EXPEDITED', 'EXPRESS'];
    if (validEnums.includes(shippingMethod.toUpperCase())) {
      return shippingMethod.toUpperCase();
    }
    return 'MAIL'; // Default fallback
  }
  
  // If it's an object with a level property that's a valid enum, use it
  if (typeof shippingMethod === 'object') {
    // If object already has a level property in the right format
    if (shippingMethod.level && typeof shippingMethod.level === 'string') {
      const level = shippingMethod.level.toUpperCase();
      if (['MAIL', 'PRIORITY_MAIL', 'GROUND_HD', 'GROUND_BUS', 'GROUND', 'EXPEDITED', 'EXPRESS'].includes(level)) {
        return level;
      }
    }
    
    // Otherwise try to infer from carrier_service_name
    if (shippingMethod.carrier_service_name) {
      const name = shippingMethod.carrier_service_name.toUpperCase();
      
      if (name.includes('PRIORITY')) {
        return 'PRIORITY_MAIL';
      } else if (name.includes('EXPRESS')) {
        return 'EXPRESS';
      } else if (name.includes('GROUND')) {
        if (name.includes('HD')) {
          return 'GROUND_HD';
        } else if (name.includes('BUS')) {
          return 'GROUND_BUS';
        } else {
          return 'GROUND';
        }
      } else if (name.includes('EXPEDITED')) {
        return 'EXPEDITED';
      }
    }
  }
  
  // Default fallback
  return 'MAIL';
}

export async function createPrintJob(orderData) {
  try {
    // Prepare the print job data structure - identical for both environments
    const printJobData = {
      contact_email: orderData.email,
      external_id: `order-${Date.now()}`,
      line_items: [
        {
          external_id: `item-${Date.now()}`,
          printable_normalization: {
            cover: {
              source_url: orderData.coverPdfUrl,
            },
            interior: {
              source_url: orderData.interiorPdfUrl,
            },
            pod_package_id: orderData.printSpecId || '0600X0900BWSTDPB060UW444MXX',
          },
          quantity: orderData.quantity,
          title: orderData.title,
        },
      ],
      shipping_address: orderData.shippingAddress,
      shipping_level: mapShippingLevelToEnum(orderData.shippingLevel),
      production_delay: 120,
    };

    // Determine the environment to use
    const environment = LULU_API.USE_SANDBOX ? 'sandbox' : 'production';
    console.log(`📚 Creating print job in ${environment.toUpperCase()} environment`);
    console.log('📚 Print job data prepared:', JSON.stringify(printJobData, null, 2));
    
    let responseData;
    
    // First try with the serverless function to avoid CORS issues
    try {
      console.log('📚 Using serverless function for print job creation');
      const serverlessFunctionUrl = '/.netlify/functions/lulu-create-print-job';
      
      const response = await fetch(serverlessFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          printJobData
        })
      });
      
      console.log('📚 Serverless print job response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Serverless print job creation failed:', errorText);
        throw new Error(`Serverless print job creation failed: ${errorText}`);
      }
      
      responseData = await response.json();
      console.log('📚 Print job created successfully via serverless function');
    } catch (serverlessError) {
      console.error('Serverless function error:', serverlessError.message);
      console.log('🔄 Falling back to direct API call with CORS proxy...');
      
      // Only as fallback, try direct API with CORS proxy
      const accessToken = await getAccessToken();
      
      // Use the appropriate endpoint based on environment
      const endpoint = '/print-jobs/';
      console.log(`🔍 Using ${LULU_API.USE_SANDBOX ? 'SANDBOX' : 'PRODUCTION'} Lulu API endpoint`);
      console.log(`🔍 API Base URL: ${LULU_API.BASE_URL}`);
      
      // Build the target URL and use CORS proxy
      const corsProxyUrl = 'https://corsproxy.io/?';
      const targetUrl = `${LULU_API.BASE_URL}${endpoint}`;
      const proxyUrl = `${corsProxyUrl}${encodeURIComponent(targetUrl)}`;
      console.log('🔍 Using CORS proxy for create print job');
      console.log('🔍 Full proxy URL:', proxyUrl);
      
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(printJobData),
      });
      
      console.log('🔍 Create print job response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Print job error response:', errorText);
        let errorMessage = 'Print job creation failed';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      responseData = await response.json();
      console.log('📚 Print job created successfully via CORS proxy');
    }
    
    // Create result object from the response data
    const result = {
      success: true,
      printJobId: responseData.id,
      data: responseData,
    };

    // Save print job record to Firebase
    console.log('Saving print job to Firebase with data:', {
      ...result,
      environment: LULU_API.USE_SANDBOX ? 'sandbox' : 'production',
    });

    try {
      const functions = getFunctions();
      const savePrintJob = httpsCallable(functions, 'savePrintJobCallable');
      
      // Extract needed data from orderData
      const { 
        email, 
        title, 
        quantity, 
        printSpecId, 
        shippingAddress, 
        shippingLevel,
        bookId
      } = orderData;

      // Create a clean orderDetails object without undefined values
      const orderDetails = {
        email,
        title,
        quantity,
        printSpecId,
        shippingAddress: shippingAddress || {},
        shippingOption: shippingLevel ? {
          id: shippingLevel.id,
          carrier_service_name: shippingLevel.carrier_service_name,
          level: shippingLevel.level,
          cost_excl_tax: shippingLevel.cost_excl_tax,
          currency: shippingLevel.currency
        } : null,
      };
      
      // Create clean shippingDetails object for Firestore
      const shippingDetails = {
        address: shippingAddress ? {
          name: shippingAddress.name || '',
          street1: shippingAddress.street1 || '',
          street2: shippingAddress.street2 || '',
          city: shippingAddress.city || '',
          country_code: shippingAddress.country_code || '',
          state_code: shippingAddress.state_code || '',
          postcode: shippingAddress.postcode || '',
          phone_number: shippingAddress.phone_number || ''
        } : {},
        option: shippingLevel ? {
          id: shippingLevel.id || 0,
          name: shippingLevel.carrier_service_name || '',
          level: shippingLevel.level || 'MAIL',
          cost: shippingLevel.cost_excl_tax || 0,
          currency: shippingLevel.currency || 'USD'
        } : {
          id: 0,
          name: 'Standard Shipping',
          level: 'MAIL',
          cost: 0,
          currency: 'USD'
        }
      };
      
      const firestoreData = {
        ...result,
        environment: LULU_API.USE_SANDBOX ? 'sandbox' : 'production',
        orderData: {
          ...orderData,
          coverPdfUrl: '[truncated]',
          interiorPdfUrl: '[truncated]',
        },
        // Add clean objects that Firestore can handle
        orderDetails,
        shippingDetails,
        createdAt: new Date().toISOString(),
        // Explicitly include these required fields at the top level for the Firebase function
        printJobId: result.printJobId,
        bookId: orderData.bookId,
      };

      console.log('Sending data to savePrintJobCallable with printJobId and bookId:', {
        printJobId: result.printJobId,
        bookId: orderData.bookId,
        orderDetailsPresent: !!orderDetails,
        shippingDetailsPresent: !!shippingDetails
      });
      
      await savePrintJob(firestoreData);
      console.log('Print job saved to Firebase');
    } catch (dbError) {
      console.error('Failed to save print job to Firebase:', dbError);
      // Continue with result even if Firebase save fails
      console.error('Error details:', {
        code: dbError.code,
        message: dbError.message,
        details: dbError.details
      });
      throw dbError;
    }

    return result;
  } catch (error) {
    console.error('Error creating print job:', error);
    throw new Error(error.message || 'Failed to create print job');
  }
}

/**
 * Gets print job details by ID, using different URLs based on environment
 * Production mode uses the developer portal URL for better visualization
 * Sandbox/development mode uses the API directly
 * 
 * @param {string} printJobId - The ID of the print job to retrieve
 * @param {boolean} viewInBrowser - If true, opens developer portal URL in browser instead of API call
 * @returns {Promise<Object|string>} Print job details or URL if viewInBrowser=true
 */
export async function getPrintJobByID(printJobId, viewInBrowser = false) {
  // For production environment, direct to the developer portal
  if (!LULU_API.USE_SANDBOX) {
    const devPortalUrl = `https://developers.lulu.com/print-jobs/detail/${printJobId}`;
    
    if (viewInBrowser) {
      // For browser viewing, return the URL that can be opened in a new tab/window
      return devPortalUrl;
    }
    
    // If not viewing in browser, redirect to the developer portal
    window.open(devPortalUrl, '_blank');
    return { redirected: true, url: devPortalUrl };
  }
  
  // For sandbox/development environment, use the API endpoint
  try {
    // Reuse the existing getPrintJobStatus function which already handles authentication
    return await getPrintJobStatus(printJobId);
  } catch (error) {
    console.error(`Error getting print job ${printJobId} details:`, error);
    throw error;
  }
}

/**
 * Get available shipping options for a given address and book details
 * This function calls out to the serverless function or uses CORS proxies as fallback
 */
export async function getShippingOptions({ shippingAddress, quantity, pageCount }) {
  console.log('🚀 getShippingOptions called with:', {
    shippingAddress,
    quantity,
  });

  try {
    const environment = LULU_API.USE_SANDBOX ? 'sandbox' : 'production';
    console.log(`📦 Using ${environment} environment for shipping options`);
    
    // Prepare the shipping address for the request
    const requestShippingAddress = {
      street1: shippingAddress.street1,
      city: shippingAddress.city,
      country: shippingAddress.country_code,
      country_code: shippingAddress.country_code,
      postcode: shippingAddress.postcode,
      phone_number: shippingAddress.phone_number || '844-212-0689',
      name: shippingAddress.name || 'Customer Name',
    };

    if (shippingAddress.state_code) {
      requestShippingAddress.state_code = shippingAddress.state_code;
    }

    // Use a default POD package for shipping costs
    const requestBody = {
      line_items: [
        {
          pod_package_id: '0600X0900BWSTDPB060UW444MXX',
          page_count: (pageCount || 32) + 1,
          quantity: quantity,
        },
      ],
      shipping_address: requestShippingAddress,
    };
    
    // Use serverless function approach - most reliable
    try {
      console.log('📦 Using serverless function for shipping options');
      
      const response = await fetch('/.netlify/functions/lulu-shipping-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Serverless function error:', errorText);
        throw new Error(`Failed to get shipping options: ${errorText}`);
      }
      
      const responseData = await response.json();
      console.log('✅ Got shipping options:', responseData);
      return responseData;
    } catch (serverlessError) {
      console.error('❌ Serverless function error:', serverlessError.message);
      throw new Error(`Failed to get shipping options: ${serverlessError.message}`);
    }
  } catch (error) {
    console.error('❌ getShippingOptions error:', error);
    throw new Error(`Failed to get shipping options: ${error.message}`);
  }
}

export async function createPrintJobCostCalculation({
  quantity,
  countryCode,
  shippingMethod,
  name,
  street1,
  street2,
  city,
  stateCode,
  postalCode,
  phoneNumber,
  podPackageId,
  pageCount,
}) {
  try {
    console.log('🧮 Calculating print job cost with parameters:', {
      quantity,
      countryCode,
      shippingMethod,
      podPackageId,
    });

    // Get a fresh access token for the cost calculation
    const accessToken = await getAccessToken();
    console.log('✅ Got access token for cost calculation');

    // Prepare the cost calculation request
    // Address fields needed for shipping cost calculation
    const shippingAddress = {
      name: name || 'Customer Name',
      street1: street1 || '123 Main St',
      city: city || 'New York',
      country_code: countryCode || 'US',
      phone_number: phoneNumber || '123-456-7890',
      postcode: postalCode || '10001',
    };

    // Add optional address fields if present
    if (street2) {
      shippingAddress.street2 = street2;
    }
    
    if (stateCode) {
      shippingAddress.state_code = stateCode;
    }

    // Prepare the API request body
    const requestBody = {
      line_items: [
        {
          pod_package_id: podPackageId || '0600X0900BWSTDPB060UW444MXX', // Default paperback 6x9
          quantity: quantity || 1,
          page_count: pageCount || 100, // Add page count which is required by Lulu API
        },
      ],
      shipping_address: shippingAddress,
    };
    
    // Add optional shipping method if provided
    if (shippingMethod) {
      // Map shipping method to one of the required Lulu API enum values
      // Allowed: "MAIL" "PRIORITY_MAIL" "GROUND_HD" "GROUND_BUS" "GROUND" "EXPEDITED" "EXPRESS"
      const VALID = ['MAIL','PRIORITY_MAIL','GROUND_HD','GROUND_BUS','GROUND','EXPEDITED','EXPRESS'];

      // 1) If level already provided, trust it
      let levelFromObject = '';
      if (typeof shippingMethod === 'object') {
        levelFromObject = (shippingMethod.level || shippingMethod.level_enum || '').toString().toUpperCase();
      } else if (typeof shippingMethod === 'string') {
        levelFromObject = shippingMethod.toString().toUpperCase();
      }

      let shippingEnum = VALID.includes(levelFromObject) ? levelFromObject : '';

      // 2) Heuristic fallback from carrier_service_name/title when level is missing
      if (!shippingEnum) {
        const nameRaw = (shippingMethod.carrier_service_name || shippingMethod.title || '').toString();
        const name = nameRaw.toUpperCase();

        if (name.includes('PRIORITY')) {
          // e.g., FedEx International Priority
          shippingEnum = 'EXPRESS';
        } else if (name.includes('EXPRESS')) {
          shippingEnum = 'EXPRESS';
        } else if (name.includes('CONNECT')) {
          // e.g., FedEx International Connect Plus
          shippingEnum = 'EXPEDITED';
        } else if (name.includes('EXPEDITED')) {
          shippingEnum = 'EXPEDITED';
        } else if (name.includes('GROUND')) {
          if (name.includes('HD')) shippingEnum = 'GROUND_HD';
          else if (name.includes('BUS')) shippingEnum = 'GROUND_BUS';
          else shippingEnum = 'GROUND';
        } else if (name.includes('MAIL') || name.includes('CROSS BORDER')) {
          // e.g., Cross Border Mail Tracked-DDU
          shippingEnum = 'MAIL';
        } else {
          // Sensible default
          shippingEnum = 'MAIL';
        }
      }

      requestBody.shipping_option = shippingEnum;
      console.log('💼 Using shipping_option enum:', requestBody.shipping_option, 'from', {
        levelFromObject,
        carrier_service_name: shippingMethod.carrier_service_name,
        title: shippingMethod.title
      });
    }

    console.log('🧮 Cost calculation request body:', JSON.stringify(requestBody, null, 2));
    
    // Use serverless function for cost calculation instead of direct API call
    console.log('🧮 Using serverless function for cost calculation');
    const serverlessFunctionUrl = '/.netlify/functions/lulu-cost-calculation';
    
    // Format the shipping address for the API
    const apiRequestBody = {
      line_items: requestBody.line_items,
      shipping_address: {
        ...requestBody.shipping_address,
        // Ensure country field is present (required by API)
        country: requestBody.shipping_address.country_code
      },
      // Format shipping_option properly
      shipping_option: requestBody.shipping_option
    };
    
    console.log('🧮 Formatted request body:', JSON.stringify(apiRequestBody, null, 2));
    
    const response = await fetch(serverlessFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiRequestBody),
    });

    console.log('🧮 Cost calculation response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Cost calculation error:', errorText);
      throw new Error(`Failed to calculate print job cost: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('🧮 Cost calculation successful:', data);
    
    return data;
  } catch (error) {
    console.error('❌ createPrintJobCostCalculation error:', error);
    throw error;
  }
}

// Export the config
export { LULU_API };
